import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolvePricing } from '../services/pricingResolveService';

const DEBOUNCE_MS = 450;

function fmt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
}

function parseNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Admin-side pricing resolve hook.
 *
 * Calls GET /api/v1/pricing/resolve with productId + partnerId + userId.
 * On quantity change (debounced), re-calls with quantity to get applied_pricing_slab.
 *
 * @param {{ productId: string, partnerId: string, userId: string, enabled: boolean }} opts
 */
export default function usePricingResolve({ productId, partnerId, userId, enabled = true }) {
  const [resolveData, setResolveData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState('');

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);
  const baseLoadedRef = useRef(false);

  const canFetch = enabled && Boolean(productId) && Boolean(partnerId) && Boolean(userId);

  const runResolve = useCallback(
    async (qty) => {
      if (!canFetch) return;

      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();
      const thisId = ++requestIdRef.current;

      setLoading(true);
      setError('');
      try {
        const data = await resolvePricing({
          productId,
          partnerId,
          userId,
          quantity: qty,
          signal: abortRef.current.signal,
        });
        if (thisId !== requestIdRef.current) return; // stale
        setResolveData(data);
        baseLoadedRef.current = true;
      } catch (e) {
        if (e?.name === 'CanceledError' || e?.name === 'AbortError') return;
        if (thisId !== requestIdRef.current) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load pricing.');
      } finally {
        if (thisId === requestIdRef.current) setLoading(false);
      }
    },
    [canFetch, productId, partnerId, userId]
  );

  // Initial load (no quantity) when all 3 IDs become available.
  useEffect(() => {
    if (!canFetch) {
      setResolveData(null);
      setError('');
      baseLoadedRef.current = false;
      return;
    }
    baseLoadedRef.current = false;
    runResolve(null);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // Only re-run when IDs change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, productId, partnerId, userId]);

  // Debounced re-resolve when quantity changes.
  useEffect(() => {
    if (!canFetch || !baseLoadedRef.current) return;

    clearTimeout(debounceRef.current);
    const parsed = Number(quantity);

    if (!quantity || !Number.isFinite(parsed) || parsed <= 0) {
      // Clear applied slab but keep base slabs.
      setResolveData((prev) =>
        prev ? { ...prev, applied_pricing_slab: null } : prev
      );
      return;
    }

    // Clear stale slab immediately so UI does not show previous tier price
    // while the debounced resolve request is pending.
    setResolveData((prev) =>
      prev ? { ...prev, applied_pricing_slab: null } : prev
    );

    debounceRef.current = setTimeout(() => {
      runResolve(parsed);
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [quantity, canFetch, runResolve]);

  // ── Derived values ──────────────────────────────────────────────────────────

  const pricing = useMemo(
    () => (Array.isArray(resolveData?.pricing) ? resolveData.pricing : []),
    [resolveData]
  );

  const specialPricing = useMemo(
    () => (Array.isArray(resolveData?.special_pricing) ? resolveData.special_pricing : []),
    [resolveData]
  );

  const appliedSlab = resolveData?.applied_pricing_slab ?? null;
  const canBypassSlabLimits = Boolean(resolveData?.can_bypass_slab_limits);

  const allSlabs = useMemo(() => [...pricing, ...specialPricing], [pricing, specialPricing]);

  const effectiveMinQty = useMemo(() => {
    if (!allSlabs.length) return 1;
    return Math.min(...allSlabs.map((s) => Number(s.min_qty)).filter(Number.isFinite));
  }, [allSlabs]);

  const effectiveMaxQty = useMemo(() => {
    if (!allSlabs.length) return null;
    const maxValues = allSlabs
      .map((s) => Number(s.max_qty))
      .filter((n) => Number.isFinite(n) && n > 0);
    return maxValues.length ? Math.max(...maxValues) : null;
  }, [allSlabs]);

  // Lowest unit price across regular (non-special) pricing for the header pill.
  const basePartnerPrice = useMemo(() => {
    if (!pricing.length) return null;
    const prices = pricing
      .map((s) => parseNum(s.pricing_per_unit))
      .filter((n) => n !== null);
    return prices.length ? Math.min(...prices) : null;
  }, [pricing]);

  // Current partner price per unit (from applied slab).
  const partnerPricePerUnit = useMemo(
    () => parseNum(appliedSlab?.pricing_per_unit) ?? null,
    [appliedSlab]
  );

  // Quantity validation message.
  const validationMessage = useMemo(() => {
    if (canBypassSlabLimits) return '';
    const parsed = Number(quantity);
    if (!quantity || !Number.isFinite(parsed) || parsed <= 0) return '';
    if (!allSlabs.length && resolveData) return 'No pricing tiers available for this product.';
    if (parsed < effectiveMinQty)
      return `Minimum order quantity is ${effectiveMinQty} units.`;
    if (effectiveMaxQty !== null && parsed > effectiveMaxQty)
      return `Maximum order quantity is ${effectiveMaxQty} units.`;
    return '';
  }, [canBypassSlabLimits, quantity, allSlabs, resolveData, effectiveMinQty, effectiveMaxQty]);

  // Status banner message.
  const statusMessage = useMemo(() => {
    const parsedQty = Number(quantity);
    const hasQty = Boolean(quantity) && Number.isFinite(parsedQty) && parsedQty > 0;

    if (appliedSlab?.is_this_special_price_slab) {
      const price = fmt(appliedSlab.pricing_per_unit);
      return {
        type: 'special_unlocked',
        text: `Special price unlocked${price ? ` — ${price}/unit` : ''}`,
      };
    }

    if (specialPricing.length > 0) {
      const minSpecialPrice = Math.min(
        ...specialPricing
          .map((s) => parseNum(s.pricing_per_unit))
          .filter((n) => n !== null)
      );
      const priceStr = fmt(minSpecialPrice);
      return {
        type: 'exclusive_available',
        text: `Exclusive pricing available${priceStr ? ` — sell at ${priceStr}/unit` : ''}`,
      };
    }

    if (appliedSlab && !validationMessage) {
      return { type: 'best_price', text: 'Best price applied for this order' };
    }

    if (!hasQty && basePartnerPrice !== null) {
      return {
        type: 'upsell',
        text: `Enter quantity to apply pricing — starting at ${fmt(basePartnerPrice)}/unit`,
      };
    }

    if (hasQty && validationMessage) {
      return { type: 'upsell', text: validationMessage };
    }

    return { type: 'default', text: 'Enter quantity to see applicable pricing' };
  }, [appliedSlab, specialPricing, quantity, validationMessage, basePartnerPrice]);

  const hasResolveSuccess = Boolean(resolveData) && !error;

  const canProceed = useMemo(() => {
    const parsedQty = Number(quantity);
    const baseCheck =
      hasResolveSuccess &&
      Number.isFinite(parsedQty) &&
      parsedQty > 0 &&
      !validationMessage &&
      !loading;
    if (canBypassSlabLimits) return baseCheck;
    return baseCheck && Boolean(appliedSlab);
  }, [hasResolveSuccess, quantity, validationMessage, loading, appliedSlab, canBypassSlabLimits]);

  const retry = useCallback(() => {
    const parsed = Number(quantity);
    runResolve(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
  }, [runResolve, quantity]);

  return {
    // Raw data
    pricing,
    specialPricing,
    appliedSlab,
    // Derived
    basePartnerPrice,
    partnerPricePerUnit,
    effectiveMinQty,
    effectiveMaxQty,
    validationMessage,
    statusMessage,
    hasResolveSuccess,
    canProceed,
    canBypassSlabLimits,
    // State
    loading,
    error,
    quantity,
    setQuantity,
    retry,
  };
}
